import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from '../../../store/root-reducer';
import { initializeHighAdvocacy } from '../../utils/initialize-third-party-integrations';

interface UseHighAdvocacyIntegrationWithConfigProps {
  enable?: boolean;
  onError?: (error: any) => void;
}

const useHighAdvocacyIntegrationWithConfig = ({
  enable = true,
  onError,
}: UseHighAdvocacyIntegrationWithConfigProps = {}) => {
  const { pathname } = useLocation();
  const userId = useSelector((state: RootState) => state.home.shAccountId);
  const email = useSelector((state: RootState) => state.home.email);
  const firstName = useSelector((state: RootState) => state.home.firstName);
  const lastName = useSelector((state: RootState) => state.home.lastName);

  useEffect(() => {
    if (!enable) return;

    const name = [firstName, lastName].filter(Boolean).join(' ').trim();
    if (!userId || !email || !name) return;

    let retryCount = 0;
    const maxRetries = 20; // 2 seconds total (20 * 100ms)
    const retryInterval = 100; // ms
    let timeoutId: NodeJS.Timeout | null = null;
    let initialized = false;

    const tryInitialize = () => {
      // SPA navigation: only init when the widget container is present
      const container = document.getElementById('ha-widget-container');

      if (!container) {
        retryCount += 1;
        if (retryCount < maxRetries) {
          // Retry after interval
          timeoutId = setTimeout(tryInitialize, retryInterval);
        }
        return;
      }

      // Container found, proceed with initialization
      if (initialized) return; // Prevent duplicate initialization
      initialized = true;

      // Read campaignId from data attribute, fallback to default
      const campaignId =
        container.getAttribute('data-campaign-id') ||
        process.env.REACT_APP_HIGH_ADVOCACY_CAMPAIGN_ID;

      try {
        initializeHighAdvocacy({
          containerId: 'ha-widget-container',
          campaignId,
          user: {
            userId: String(userId),
            email,
            name,
          },
          onError,
        });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('Error initializing HighAdvocacy:', error);
        }
        onError?.(error);
      }
    };

    // Start the initialization attempt
    tryInitialize();

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [enable, pathname, userId, email, firstName, lastName, onError]);

  return null;
};

export default useHighAdvocacyIntegrationWithConfig;
